import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Board, Card, boardApi, cardApi } from '../services/api';
import { CardModal } from '../components/CardModal';
import { CardOverageLockedModal } from '../components/CardOverageLockedModal';
import { PageNavbar } from '../components/PageNavbar';
import { NavArrowLeft, BoxIso, Lock } from 'iconoir-react';
import { useAuth } from '../contexts/AuthContext';
import { boardMemberApi } from '../services/api';

export const ArchivedCards: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const [board, setBoard] = useState<Board | null>(null);
  const [archivedCards, setArchivedCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [lockedCard, setLockedCard] = useState<Card | null>(null);
  const { user } = useAuth();
  const [boardMembers, setBoardMembers] = useState<any[]>([]);

  useEffect(() => {
    loadBoard();
  }, [boardId]);

  // Détecter le paramètre card dans l'URL pour ouvrir automatiquement la modale
  // (jamais CardModal pour une carte verrouillée, même via lien direct).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cardId = urlParams.get('card');
    if (cardId && archivedCards.length > 0) {
      const card = archivedCards.find(c => c.id === parseInt(cardId, 10));
      if (card) {
        if (card.locked) {
          setLockedCard(card);
        } else {
          setSelectedCard(card);
        }
      }
    }
  }, [archivedCards]);

  const handleCardClick = (card: Card) => {
    if (card.locked) {
      setLockedCard(card);
    } else {
      setSelectedCard(card);
    }
  };

  const loadBoard = async () => {
    if (!boardId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await boardApi.getOne(parseInt(boardId));
      setBoard(response.data);
      const membersResponse = await boardMemberApi.getAll(parseInt(boardId));
      setBoardMembers(membersResponse.data);
      
      // Extraire toutes les cartes archivées
      const archived: Card[] = [];
      response.data.columns.forEach(column => {
        column.cards.forEach(card => {
          if (card.archived) {
            archived.push({ ...card, column_title: column.title } as any);
          }
        });
      });
      setArchivedCards(archived);
    } catch (err: any) {
      setError('Erreur lors du chargement des cartes archivées');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardUpdate = (updatedCard: Card) => {
    setArchivedCards(prev => 
      prev.map(card => card.id === updatedCard.id ? updatedCard : card)
    );
    if (selectedCard?.id === updatedCard.id) {
      setSelectedCard(updatedCard);
    }
  };

  const handleCardDelete = (cardId: number) => {
    setArchivedCards(prev => prev.filter(card => card.id !== cardId));
    setSelectedCard(null);
  };

  const handleCardArchive = async (cardId: number, archived: boolean) => {
    try {
      // Trouver la carte et sa colonne
      const cardToUpdate = archivedCards.find(c => c.id === cardId);
      if (!cardToUpdate || !board) return;

      // Trouver la colonne de la carte
      const column = board.columns.find(col => 
        col.cards.some(c => c.id === cardId)
      );
      
      if (!column) return;

      // Mettre à jour le statut archived via l'API
      await cardApi.update(column.id, cardId, {
        archived: archived
      });

      if (!archived) {
        // Si la carte est désarchivée, la retirer de la liste
        setArchivedCards(prev => prev.filter(card => card.id !== cardId));
        setSelectedCard(null);
      }
      
      await loadBoard();
    } catch (err) {
      console.error('Erreur lors du désarchivage de la carte', err);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="archived-cards-page">
        <div className="loading-message">Chargement...</div>
      </div>
    );
  }

  if (error || !board) {
    return (
      <div className="archived-cards-page">
        <div className="error-message">{error || 'Tableau introuvable'}</div>
        <button className="btn btn-primary" onClick={handleBack}>
          Retour à l'administration
        </button>
      </div>
    );
  }

  const isOwner = user?.id === board.user_id;
  const boardMember = boardMembers.find(m => m.email === user?.email);
  const userRole = isOwner ? 'admin' : (boardMember?.role || 'visitor');

  return (
    <div className="archived-cards-page">
      <PageNavbar title={`Cartes archivées - ${board.title}`} />
      
      <div className="archived-cards-container">
        <div className="archived-cards-header">
          <button className="btn-back" onClick={handleBack}>
            <NavArrowLeft width={20} height={20} strokeWidth={2} />
            <span>Retour</span>
          </button>
        </div>

        <div className="archived-cards-content">
        {archivedCards.length === 0 ? (
          <div className="empty-message">
            <BoxIso width={48} height={48} strokeWidth={1.5} />
            <p>Aucune carte archivée</p>
          </div>
        ) : (
          <div className="archived-cards-grid">
            {archivedCards.map(card => (
              <div
                key={card.id}
                className={`archived-card${card.locked ? ' archived-card-locked' : ''}`}
                onClick={() => handleCardClick(card)}
              >
                {card.label_color && (
                  <div
                    className="archived-card-label"
                    style={{ backgroundColor: card.label_color }}
                  />
                )}
                <h3 className="archived-card-title">
                  {card.locked && (
                    <span title="Carte verrouillée : dépassement de votre offre Premium">
                      <Lock width={14} height={14} strokeWidth={2} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    </span>
                  )}
                  {card.title}
                </h3>
                {card.description && (
                  <p className="archived-card-description">
                    {card.description.substring(0, 100)}
                    {card.description.length > 100 ? '...' : ''}
                  </p>
                )}
                <div className="archived-card-meta">
                  <span className="archived-card-column">{(card as any).column_title}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {selectedCard && (
        <CardModal
          card={selectedCard}
          isOpen={true}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          onDelete={handleCardDelete}
          onArchive={handleCardArchive}
          userRole={userRole}
        />
      )}

      <CardOverageLockedModal
        isOpen={!!lockedCard}
        onClose={() => setLockedCard(null)}
        card={lockedCard}
        userRole={userRole}
        onDelete={handleCardDelete}
      />
    </div>
  );
};
